import genericApi from './genericApi';

import OfflineRfsModel, { Point, CurrentAwb, OfflineRfsOrder, TruckingCompany, RfsAwbHistory, RfsAwbInfo } from '../models/OfflineRfs';
import ApiResponse from '../models/ApiResponse';
import TruckCart from '../models/TruckCart';
import OrderDashboard from '../models/OrderDashboard';
import Awb from '../models/Awb';
import OrderWhs from '../models/OfflineOrderWhs';
import OrderWhsDetails from '../models/OrderWhsDetails';
import GhaWhPoAwbException from '../models/GhaWhPoAwbException';

const offlineRfsHandler = {

  getPoints: (ispickUp: boolean): Promise<ApiResponse<Array<Point>>> => {
    let url = `point?isPickUp=${ispickUp}`;
    return genericApi.get(url);
  },

  createPoint: (type: boolean, point: Point) : Promise<ApiResponse<Point>> => {
    return genericApi.put('point', {type, point});
  }, 

  createRfs: (offlineRfs: OfflineRfsModel) : Promise<ApiResponse<OfflineRfsModel>> => {
    return genericApi.put('createRfs', {offlineRfs});
  },

  createTruckerCompany: (truckerInfo: TruckingCompany) : Promise<ApiResponse<{id: number}>> => {
    return genericApi.put('trucker', { truckerInfo});
  },

  getTruckerCompany: (): Promise<ApiResponse<Array<TruckingCompany>>> => {
    return genericApi.get('trucker');
  },

  getOfflineRsfData: (): Promise<ApiResponse<OfflineRfsModel>> => {
    return genericApi.get('offlineRfs');
  },

  saveRfsAwb: (offlineRfsAwb: Array<CurrentAwb>, edit: boolean): Promise<ApiResponse<TruckCart>> => {
    const keyParam: string = edit ? `?edit=${edit}` : '';
    return genericApi.put(`rfsAwb${keyParam}`, {offlineRfsAwb});
  },

  saveRfsAwbToOrder: (offlineRfsAwb: Array<CurrentAwb>, rfsId: number): Promise<ApiResponse<TruckCart>> => {
    return genericApi.put(`rfsAwb`, {offlineRfsAwb, rfsId});
  },

  getOfflineRfsOrders: (startDate: string, endDate: string, startTime: string, endTime: string, keyword: string, origin: string, mvt: string, sort?: boolean): Promise<ApiResponse<Array<OrderDashboard>>> => {

    return commonPickupOrderParams('office', startDate, endDate, startTime, endTime, keyword, origin, mvt, sort);
  },

  getOfflineRfsOrderDetails: (orderId: number, key?: string): Promise<ApiResponse<OfflineRfsOrder>> => {

    const keyParam: string = key ? `?key=${key}` : '';

    return genericApi.get(`offlinerfs_order/${orderId}${keyParam}`);
  },

  getOfflineOrderWhs: (orderId: string, key?: string): Promise<ApiResponse<OrderWhsDetails >> => {

    const keyParam: string = key ? `&key=${key}` : '';

    return genericApi.get(`offlinerfs_order/${orderId}?usedFor=warehouse${keyParam}`);
  },

  updateOfflineRfsOrder: (orderInfo: OfflineRfsOrder ): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`offlinerfs_order/${orderInfo.id}`, orderInfo);
  },

  deleteOfflineOrder: (id: number): Promise<ApiResponse<boolean>> => {

    return genericApi.delete(`offlinerfs_order/${id}`)
  },

  deleteOfflineAwb: (id: number, orderNumber: number): Promise<ApiResponse<boolean>> => {

    return genericApi.delete(`rfsAwb/${orderNumber}/${id}`)
  },

  getOfflineOrderAwbs: (id: string): Promise<ApiResponse<Array<Awb>>> => {
    
    return genericApi.get(`offlinerfs_order/${id}/awbs`);
  },
  
  addRfsExportAirwayBillToCart: (awbCode: string, awbNumber: string, version: number): Promise<ApiResponse<TruckCart>> => {

    return genericApi.put(`offlinerfs/list/${awbCode}/${awbNumber}/${version}`);
  },

  getRfsAwbHistory: (awbCode: string, awbNumber: string): Promise<ApiResponse<RfsAwbHistory[]>> => {

    return genericApi.get(`offlineRfsHistory/${awbCode}/${awbNumber}`);
  },

  editRfsAwb: (awbCode: string, awbNumber: string, awbData: RfsAwbInfo): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`rfsAwb/${awbCode}/${awbNumber}`, { awbData: awbData });
  },

  getSpecialsForm: (orderId: number, awbCode: string, awbNumber: string, version: number): Promise<ApiResponse<any>> => {
    return genericApi.get(`offlinerfs_order/${orderId}/awb/${awbCode}/${awbNumber}/specials_form?version=${version}`);
  },

  getSpecialsOptions: (): Promise<ApiResponse<string[]>> => {
    return genericApi.get('offlinerfs/specials_options');
  },

  getDestinationAirlines: (location: string): Promise<ApiResponse<any>> => {
    
    return genericApi.get(`location_flights/${location}`);
  },
  getGhaTime: (id: number, seconds: number): Promise<ApiResponse<{date: string, time: string}>> => {

    return genericApi.get(`offlinerfs_order/time/${id}/${seconds}`);
  },

  setETA: (poNumber: number, key: string, time: string): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`offlinerfs_order/${poNumber}/eta?key=${key}&eta=${time}`);
  },

  getOfflineOrdersWhs: (date: string):Promise<ApiResponse<Array<OrderWhs>>> => {

    return commonPickupOrderParams('warehouse', date, '', '', '', '', '', '', true);
  },

  doGhaWhPoLock: (poNumber: number, locked: boolean, iswh?: boolean): Promise<ApiResponse<{ lock_status: string; gha_wh_user_name: string; }>> => {

    return genericApi.post(`offlinerfs_order/${poNumber}/lock`, { locked, iswh });
  },
  setGhaWhPoAwbDock: (poNumber: number, dockID: number | null): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`offlinerfs_order/${poNumber}/dock`, { dock_id: dockID });
  },
  setGhaWhPoAwbTruckOnDock: (poNumber: number): Promise<ApiResponse<boolean>> => {

    return genericApi.post(`offlinerfs_order/${poNumber}/truck_on_dock`);
  },
  setSealLockVerify: (poNumber: number, sealLockVerified: boolean | null): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`offlinerfs_order/${poNumber}/seal_lock_verify`, { seal_lock_verified: sealLockVerified });
  },
  getGhaPoAwbWhExceptions: (poNumber: string, awbCode: string, awbNumber: string): Promise<ApiResponse<GhaWhPoAwbException>> => {
    
    return genericApi.get(`offlinerfs_order/${poNumber}/awb/${awbCode}/${awbNumber}/exception`);
  },
  getGhaPoNote: (poNumber: string): Promise<ApiResponse<{ note: string, attachments: string[] }>> => {
    
    return genericApi.get(`offlinerfs_order/${poNumber}/note`);
  },
  updateGhaPoNote: (poNumber: string, data: { note: string }): Promise<ApiResponse<boolean>> => {
    
    return genericApi.put(`offlinerfs_order/${poNumber}/note`, data);
  },
};

export default offlineRfsHandler;

function commonPickupOrderParams(usedFor: string, startDate: string, endDate: string, startTime: string, endTime: string, keyword: string, origin: string, mvt: string, sort?: boolean) {

  let url: string = `offlineRfs_orders?usedFor=${usedFor}&`;

  if (startDate ) { 
    url = `${url}start_date=${startDate}&`;
  };

  if (endDate ) {
    url = `${url}end_date=${endDate}&`;
  };

  if (startTime ) {
    url = `${url}start_time=${startTime}&`;
  };

  if (endTime ) {
    url = `${url}end_time=${endTime}&`;
  };

  if (keyword ) {
    url = `${url}keyword=${keyword}&`;
  };

  if ( origin ) {
    url = `${url}origin=${origin}&`;
  };

  if ( mvt ) {
    url = `${url}mvt=${mvt}&`;
  };

  if ( sort ) {
    url = `${url}sort=true`;
  };
  
  return genericApi.get(url);
}